import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Map, { Layer, Marker, NavigationControl, Source, type MapRef, type MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Search, Trash2, Pencil, Star, Crosshair, X, Check, Navigation, Share2, Download, Upload, LocateFixed, Info, Ruler } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useFleetStore } from '@/stores/fleet';
import { haversineM, bearingDeg, compassPt } from '@/stores/navRoutes';
import { pointsApi, POINT_TYPES, pointType, type NautPoint, type PointInput } from '@/api/endpoints/points';
import { download, pointsToCSV, pointsToGeoJSON, parsePointsFile } from '@/lib/pointsIO';
import { cn } from '@/lib/utils';

async function sharePoint(p: NautPoint) {
  const text = `${p.name} — ${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}\nhttps://www.google.com/maps?q=${p.latitude},${p.longitude}`;
  const nav = navigator as Navigator & { share?: (d: { title: string; text: string }) => Promise<void> };
  try {
    if (typeof nav.share === 'function') {
      await nav.share({ title: p.name, text });
      return;
    }
    await nav.clipboard.writeText(text);
    alert('Ponto copiado para a área de transferência.');
  } catch { /* cancelado */ }
}

const STYLE: StyleSpecification = {
  version: 8,
  sources: {
    base: {
      type: 'raster',
      tiles: ['https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png', 'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png'],
      tileSize: 256,
      attribution: '© CARTO · © OpenStreetMap',
    },
  },
  layers: [{ id: 'base', type: 'raster', source: 'base' }],
};

interface Draft {
  id?: number;
  lat: number;
  lon: number;
  name: string;
  type: string;
  description: string;
  visibility: string;
}

export function PointsPage() {
  const queryClient = useQueryClient();
  const positions = useFleetStore((s) => s.positions);
  const mapRef = useRef<MapRef | null>(null);

  const ref = useMemo(() => {
    let best: { lat: number; lon: number; t: number } | null = null;
    for (const p of positions.values()) {
      if (p.latitude == null || p.longitude == null) continue;
      const t = p.fixTime ? new Date(p.fixTime).getTime() : 0;
      if (!best || t > best.t) best = { lat: p.latitude, lon: p.longitude, t };
    }
    return best ?? { lat: -22.9, lon: -43.16, t: 0 };
  }, [positions]);

  const query = useQuery({
    queryKey: ['points', ref.lat.toFixed(2), ref.lon.toFixed(2)],
    queryFn: () => pointsApi.nearby(ref.lat, ref.lon),
  });
  const points = query.data ?? [];

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [view, setView] = useState<'todos' | 'favoritos' | 'proximos'>('todos');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Régua: medir distância entre 2 pontos.
  const [measureA, setMeasureA] = useState<NautPoint | null>(null);
  const [measureB, setMeasureB] = useState<NautPoint | null>(null);

  const pickMeasure = (p: NautPoint) => {
    if (!measureA || (measureA && measureB)) { setMeasureA(p); setMeasureB(null); }
    else if (p.id !== measureA.id) setMeasureB(p);
  };
  const measure = useMemo(() => {
    if (!measureA || !measureB) return null;
    const a = { lat: measureA.latitude, lon: measureA.longitude };
    const b = { lat: measureB.latitude, lon: measureB.longitude };
    const m = haversineM(a, b);
    return { nm: m / 1852, km: m / 1000, dir: compassPt(bearingDeg(a, b)) };
  }, [measureA, measureB]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = points.filter(
      (p) =>
        (typeFilter === 'all' || p.type === typeFilter) &&
        (!q || p.name.toLowerCase().includes(q)),
    );
    if (view === 'favoritos') list = list.filter((p) => p.is_favorite);
    // 'proximos' e 'todos' já vêm ordenados por distância (endpoint nearby).
    return list;
  }, [points, search, typeFilter, view]);

  const favCount = useMemo(() => points.filter((p) => p.is_favorite).length, [points]);

  const onMapClick = (e: MapLayerMouseEvent) => {
    setSelectedId(null);
    setDraft({ lat: e.lngLat.lat, lon: e.lngLat.lng, name: '', type: 'pesca', description: '', visibility: 'particular' });
  };

  const [gpsBusy, setGpsBusy] = useState(false);
  const useGps = () => {
    if (!('geolocation' in navigator)) { alert('GPS não disponível neste dispositivo.'); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsBusy(false);
        setSelectedId(null);
        setDraft({ lat: pos.coords.latitude, lon: pos.coords.longitude, name: '', type: 'pesca', description: '', visibility: 'particular' });
        mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 15, duration: 700 });
      },
      (err) => { setGpsBusy(false); alert(err.code === 1 ? 'Permissão de GPS negada.' : 'Não foi possível obter sua posição.'); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
    );
  };

  const saveMutation = useMutation({
    mutationFn: (d: Draft) => {
      const body: PointInput = {
        name: d.name.trim(),
        type: d.type,
        description: d.description.trim() || null,
        latitude: d.lat,
        longitude: d.lon,
        visibility: d.visibility,
        color: pointType(d.type).color,
      };
      return d.id ? pointsApi.update(d.id, body) : pointsApi.create(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['points'] });
      setDraft(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => pointsApi.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text();
      const parsed = parsePointsFile(file.name, text);
      let ok = 0;
      for (const pt of parsed) {
        try { await pointsApi.create(pt); ok++; } catch { /* ignora falha individual */ }
      }
      return { ok, total: parsed.length };
    },
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['points'] });
      alert(`Importados ${res.ok}/${res.total} pontos.`);
    },
  });

  const onImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) importMutation.mutate(f);
    e.target.value = '';
  };

  const favMutation = useMutation({
    mutationFn: (p: NautPoint) =>
      pointsApi.update(p.id, {
        name: p.name, type: p.type, description: p.description,
        latitude: p.latitude, longitude: p.longitude, visibility: p.visibility,
        icon: p.icon, color: p.color, is_favorite: !p.is_favorite, source: p.source,
      }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['points'] }); },
  });

  const focus = (p: NautPoint) => {
    setSelectedId(p.id);
    mapRef.current?.flyTo({ center: [p.longitude, p.latitude], zoom: 14, duration: 600 });
  };

  const startEdit = (p: NautPoint) => {
    setDraft({ id: p.id, lat: p.latitude, lon: p.longitude, name: p.name, type: p.type ?? 'personalizado', description: p.description ?? '', visibility: p.visibility });
  };

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Mapa */}
      <div className="relative h-72 lg:h-full lg:flex-1">
        <Map
          ref={mapRef}
          initialViewState={{ longitude: ref.lon, latitude: ref.lat, zoom: 11 }}
          mapStyle={STYLE}
          style={{ width: '100%', height: '100%' }}
          attributionControl={false}
          onClick={onMapClick}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <Source id="seamark" type="raster" tiles={['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png']} tileSize={256}>
            <Layer id="seamark-l" type="raster" paint={{ 'raster-opacity': 0.9 }} />
          </Source>

          {filtered.map((p) => {
            const t = pointType(p.type);
            return (
              <Marker key={p.id} longitude={p.longitude} latitude={p.latitude} anchor="bottom"
                onClick={(e) => { e.originalEvent.stopPropagation(); focus(p); }}>
                <span
                  className={cn('flex items-center justify-center rounded-full text-[13px] shadow ring-2 ring-white', selectedId === p.id ? 'h-8 w-8' : 'h-6 w-6')}
                  style={{ background: t.color }}
                  title={p.name}
                >
                  {t.emoji}
                </span>
              </Marker>
            );
          })}

          {draft && (
            <Marker longitude={draft.lon} latitude={draft.lat} anchor="bottom">
              <MapPin className="h-7 w-7 text-status-blocked drop-shadow" />
            </Marker>
          )}
        </Map>
        <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-card/95 px-3 py-1 text-xs font-semibold shadow ring-1 ring-border">
          Toque no mapa ou use o GPS para salvar um ponto
        </div>
        <button
          type="button"
          onClick={useGps}
          disabled={gpsBusy}
          className="absolute bottom-4 right-3 inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2.5 text-sm font-semibold text-brand-foreground shadow-lg hover:bg-brand/90 disabled:opacity-60"
          title="Salvar minha posição atual (GPS)"
        >
          <LocateFixed className={cn('h-4 w-4', gpsBusy && 'animate-pulse')} />
          {gpsBusy ? 'Localizando…' : 'Minha posição (GPS)'}
        </button>
      </div>

      {/* Painel lateral */}
      <aside className="flex w-full flex-col border-t border-border bg-card lg:w-[380px] lg:border-l lg:border-t-0">
        <header className="space-y-2 border-b border-border p-3">
          <div className="flex items-center justify-between">
            <h1 className="text-sm font-bold">Pontos Náuticos <span className="text-muted-foreground">· {filtered.length}</span></h1>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar ponto" className="rounded-full bg-secondary pl-9" />
          </div>
          <div className="flex gap-1">
            {([['todos', `Todos ${points.length}`], ['proximos', 'Próximos'], ['favoritos', `★ ${favCount}`]] as const).map(([k, lbl]) => (
              <button key={k} type="button" onClick={() => setView(k)}
                className={cn('flex-1 rounded-md px-2 py-1 text-[11px] font-semibold', view === k ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-accent')}>
                {lbl}
              </button>
            ))}
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="all">Todos os tipos</option>
            {POINT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.emoji} {t.label}</option>)}
          </select>
          <div className="flex flex-wrap gap-1.5">
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-secondary px-2.5 py-1 text-[11px] font-semibold hover:bg-accent">
              <Upload className="h-3.5 w-3.5" /> {importMutation.isPending ? 'Importando…' : 'Importar'}
              <input type="file" accept=".csv,.geojson,.json,.kml" className="hidden" onChange={onImportFile} />
            </label>
            <button type="button" onClick={() => download('pontos.geojson', pointsToGeoJSON(points), 'application/geo+json')} disabled={!points.length} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2.5 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"><Download className="h-3.5 w-3.5" /> GeoJSON</button>
            <button type="button" onClick={() => download('pontos.csv', pointsToCSV(points), 'text/csv')} disabled={!points.length} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2.5 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"><Download className="h-3.5 w-3.5" /> CSV</button>
          </div>
        </header>

        {draft && (
          <DraftForm
            draft={draft}
            setDraft={setDraft}
            onCancel={() => setDraft(null)}
            onSave={() => saveMutation.mutate(draft)}
            saving={saveMutation.isPending}
          />
        )}

        {(measureA || measureB) && (
          <div className="flex items-center gap-2 border-b border-border bg-primary/5 px-3 py-2 text-[11px]">
            <Ruler className="h-3.5 w-3.5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              {measure ? (
                <span className="font-semibold text-primary">
                  {measureA!.name} → {measureB!.name}: {measure.nm.toFixed(2).replace('.', ',')} mn ({measure.km.toFixed(1).replace('.', ',')} km) a {measure.dir}
                </span>
              ) : (
                <span className="text-muted-foreground">Origem: <b>{measureA?.name}</b> — escolha o 2º ponto na régua.</span>
              )}
            </div>
            <button type="button" onClick={() => { setMeasureA(null); setMeasureB(null); }} className="rounded p-1 text-muted-foreground hover:bg-accent" title="Limpar medição"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}

        <ul className="flex-1 divide-y divide-border overflow-y-auto">
          {query.isLoading && <li className="p-4 text-sm text-muted-foreground">Carregando pontos…</li>}
          {query.error && <li className="p-4 text-sm text-destructive">Erro ao carregar (faça login).</li>}
          {!query.isLoading && filtered.length === 0 && (
            <li className="p-4 text-center text-xs text-muted-foreground">Nenhum ponto. Toque no mapa para criar.</li>
          )}
          {filtered.map((p) => {
            const t = pointType(p.type);
            return (
              <li key={p.id} className={cn('flex items-start gap-3 p-3', selectedId === p.id && 'bg-primary/5')}>
                <button type="button" onClick={() => focus(p)} className="flex flex-1 items-start gap-3 text-left">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm" style={{ background: `${t.color}22` }}>{t.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{t.label}</p>
                    {p.distance_nm != null && (
                      <p className="text-[11px] font-semibold text-primary">
                        {p.distance_nm.toFixed(1).replace('.', ',')} mn {p.bearing ? `a ${p.bearing}` : ''} da posição atual
                      </p>
                    )}
                  </div>
                </button>
                <div className="flex shrink-0 gap-1">
                  <Link to={`/points/${p.id}`} className="rounded p-1.5 text-muted-foreground hover:bg-accent" title="Detalhes"><Info className="h-4 w-4" /></Link>
                  <button
                    type="button"
                    onClick={() => pickMeasure(p)}
                    className={cn('rounded p-1.5 hover:bg-accent', (measureA?.id === p.id || measureB?.id === p.id) ? 'text-primary' : 'text-muted-foreground')}
                    title="Medir distância (régua)"
                  >
                    <Ruler className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => favMutation.mutate(p)} className="rounded p-1.5 hover:bg-accent" title={p.is_favorite ? 'Remover dos favoritos' : 'Favoritar'}>
                    <Star className={cn('h-4 w-4', p.is_favorite ? 'fill-status-ready text-status-ready' : 'text-muted-foreground')} />
                  </button>
                  <Link
                    to={`/navigator?dlat=${p.latitude}&dlon=${p.longitude}&dname=${encodeURIComponent(p.name)}`}
                    className="rounded p-1.5 text-primary hover:bg-primary/10"
                    title="Navegar até este ponto"
                  >
                    <Navigation className="h-4 w-4" />
                  </Link>
                  <button type="button" onClick={() => sharePoint(p)} className="rounded p-1.5 text-muted-foreground hover:bg-accent" title="Compartilhar"><Share2 className="h-4 w-4" /></button>
                  <button type="button" onClick={() => startEdit(p)} className="rounded p-1.5 text-muted-foreground hover:bg-accent" title="Editar"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => { if (window.confirm(`Excluir "${p.name}"?`)) deleteMutation.mutate(p.id); }} className="rounded p-1.5 text-muted-foreground hover:bg-accent" title="Excluir"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>
    </div>
  );
}

function DraftForm({ draft, setDraft, onCancel, onSave, saving }: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const up = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v });
  return (
    <Card className="m-3">
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold">{draft.id ? 'Editar ponto' : 'Novo ponto'}</p>
          <button type="button" onClick={onCancel} className="rounded p-1 text-muted-foreground hover:bg-accent"><X className="h-4 w-4" /></button>
        </div>
        <Input value={draft.name} onChange={(e) => up('name', e.target.value)} placeholder="Nome do ponto" autoFocus />
        <select value={draft.type} onChange={(e) => up('type', e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
          {POINT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.emoji} {t.label}</option>)}
        </select>
        <Input value={draft.description} onChange={(e) => up('description', e.target.value)} placeholder="Observação (opcional)" />
        <select value={draft.visibility} onChange={(e) => up('visibility', e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
          <option value="particular">Particular</option>
          <option value="empresa">Empresa</option>
          <option value="marina">Marina</option>
          <option value="compartilhado">Compartilhado</option>
        </select>
        <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <Crosshair className="h-3 w-3" /> {draft.lat.toFixed(5)}, {draft.lon.toFixed(5)}
        </p>
        <Button onClick={onSave} disabled={!draft.name.trim() || saving} className="w-full gap-1.5">
          <Check className="h-4 w-4" /> {saving ? 'Salvando…' : draft.id ? 'Salvar' : 'Salvar ponto'}
        </Button>
      </CardContent>
    </Card>
  );
}
